import OverviewCard from '../OverviewCard.tsx'
import { routes } from 'types/routes.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { useGetCustomerAccountingQuery } from 'store/queries/bolt-backend/customers'
import { INVOICE_STATUS_CONFIG } from 'pages/app/billing/invoices/status-banner/constants.ts'
import { getInvoiceStatus } from 'pages/app/billing/invoices/status-banner/utils.ts'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import parse from 'html-react-parser'
import { formatCurrency } from 'utils/format.ts'
import { InvoicesStatus } from 'pages/app/billing/invoices/status-banner/types.ts'
import styles from 'pages/app/overview/overview-cards/invoices/InvoicesOverviewCard.module.scss'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import classNames from 'classnames'
import { selectCurrentAccount, selectCurrentAccountHasContractWithSubscriptionDateOlderThanOneDay } from 'store/contact/slice.ts'
import { isCustomerInvoice } from 'pages/app/billing/invoices/utils.ts'

const InvoicesOverviewCard = () => {
  // Redux
  const { selected } = useStoreSelector((store) => store.contact)
  const account = useStoreSelector(selectCurrentAccount)
  const hasContractOlderThanOneDay = useStoreSelector(selectCurrentAccountHasContractWithSubscriptionDateOlderThanOneDay)

  // Redux Queries
  const { data, isLoading, isFetching, isError } = useGetCustomerAccountingQuery(
    { customerNumber: selected.account },
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: !selected.account || !hasContractOlderThanOneDay
    }
  )

  // i18n
  const { t } = useTranslation('invoices')
  const language = useGetLanguage()

  // Constants
  const loading = !data && (isLoading || isFetching)
  const usesDirectDebit = !!account?.paymentDetails?.directDebit
  const expiredInvoices = data?.documents.filter((document) => {
    if (!isCustomerInvoice(document)) return false
    const invoiceDueDate = dayjs(document.dueDate)
    return dayjs().isAfter(invoiceDueDate.add(4, 'days'), 'day') && !document.paid
  })

  const status = getInvoiceStatus({
    balance: data?.balance,
    usesDirectDebit,
    hasExpiredInvoices: !!expiredInvoices?.length
  })

  if (
    data?.hasBddubDocuments ||
    isError ||
    status === InvoicesStatus.BREAK_EVEN ||
    (usesDirectDebit && status !== InvoicesStatus.DIRECT_DEBIT_WITH_UNPAID_INVOICES) ||
    !status
  ) {
    return null
  }

  return (
    <OverviewCard
      isLoading={loading}
      color="white"
      button={{
        label: t(($) => $.invoices.button, {
          ns: 'overview'
        }),
        href: routes.BILLING_INVOICES
      }}
      className={classNames(styles.card, `is-${INVOICE_STATUS_CONFIG[status]?.color}-card`)}
    >
      {loading ? (
        <LoadingSkeleton.Paragraph lines={2} />
      ) : (
        <OverviewCard.Title variant="h5">
          {parse(
            t(($) => $.invoicesStatusBanner[INVOICE_STATUS_CONFIG[status].textKey].title, {
              amount: formatCurrency(Number(data?.balance?.customer), { language })
            })
          )}
        </OverviewCard.Title>
      )}
    </OverviewCard>
  )
}

export default InvoicesOverviewCard
